/**
 * Browser-side device identity for OpenClaw gateway pairings.
 *
 * Implements the OpenClaw v2 device-auth handshake (see
 * https://github.com/openclaw/openclaw — `buildDeviceAuthPayload` in
 * `src/gateway/device-auth.ts`):
 *
 *   - Ed25519 keypair generated with `@noble/curves` and persisted to
 *     localStorage (raw bytes, base64-encoded)
 *   - `deviceId = SHA-256(rawPublicKey).hex` — must match the server's
 *     `deriveDeviceIdFromPublicKey()` exactly
 *   - Signature is over the canonical v2 payload string:
 *       `v2|deviceId|clientId|clientMode|role|scopes|signedAtMs|token|nonce`
 *     UTF-8 encoded; the signature is the raw 64-byte Ed25519 output,
 *     base64-encoded.
 *   - `signedAt` is Unix epoch **milliseconds** (integer).
 *
 * The gateway verifies `device: { id, publicKey, signature, signedAt, nonce }`
 * on each connect, letting it rotate pairing tokens without a re-approval.
 *
 * @note Target audience: Paradigm/Paradex traders. Communication should be
 * concise and information-dense unless specifically asked for explanation.
 */

import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

const STORAGE_KEY = 'paradigm_terminal_device';

/** Version tag for the signed auth payload. */
const DEVICE_AUTH_PAYLOAD_VERSION = 'v2';

interface StoredDevice {
  readonly id: string; // hex SHA-256 of raw public key
  readonly privateKey: string; // base64 of raw 32 bytes
  readonly publicKey: string; // base64 of raw 32 bytes
}

export interface DeviceIdentity {
  readonly id: string;
  /** Base64-encoded raw 32-byte Ed25519 public key. */
  readonly publicKey: string;
  /** Signs a UTF-8 payload; returns base64 of the raw 64-byte signature. */
  readonly sign: (payload: string) => string;
}

let cached: DeviceIdentity | null = null;

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCodePoint(b);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.codePointAt(i)!;
  return out;
}

export function getDeviceIdentity(): DeviceIdentity {
  if (cached == null) cached = loadOrCreate();
  return cached;
}

function loadOrCreate(): DeviceIdentity {
  let stored: StoredDevice | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw != null) stored = JSON.parse(raw) as StoredDevice;
  } catch {
    stored = null;
  }

  if (
    stored == null ||
    typeof stored.id !== 'string' ||
    typeof stored.privateKey !== 'string' ||
    typeof stored.publicKey !== 'string'
  ) {
    const privateKey = ed25519.utils.randomPrivateKey();
    const publicKey = ed25519.getPublicKey(privateKey);
    const id = bytesToHex(sha256(publicKey));
    stored = {
      id,
      privateKey: bytesToBase64(privateKey),
      publicKey: bytesToBase64(publicKey),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Non-persistent identity; signing still works for this session.
    }
  }

  const privBytes = base64ToBytes(stored.privateKey);

  return {
    id: stored.id,
    publicKey: stored.publicKey,
    sign(payload: string): string {
      const sig = ed25519.sign(new TextEncoder().encode(payload), privBytes);
      return bytesToBase64(sig);
    },
  };
}

export interface DeviceHandshakeInput {
  readonly clientId: string;
  /** Client mode — e.g. `'webchat'` for the browser-based terminal. */
  readonly clientMode: string;
  /** Operator role — `'operator'` or `'pairing'`. */
  readonly role: string;
  /** Scopes as comma-joined string, in the exact order the client sends them. */
  readonly scopes: readonly string[];
  /** Auth token; empty string when in pairing mode (unpaired first connect). */
  readonly token: string;
  /** Server-provided nonce from `connect.challenge`. */
  readonly nonce: string;
}

export interface DeviceHandshake {
  readonly id: string;
  /** Base64-encoded raw 32-byte Ed25519 public key. */
  readonly publicKey: string;
  /** Base64-encoded raw 64-byte Ed25519 signature over the v2 payload. */
  readonly signature: string;
  /** Unix timestamp in milliseconds (integer). */
  readonly signedAt: number;
  readonly nonce: string;
}

/**
 * Build the signed `device` field for a gateway connect request.
 *
 * Canonical v2 signed payload:
 *   `v2|deviceId|clientId|clientMode|role|scopes|signedAtMs|token|nonce`
 */
export function buildDeviceHandshake(
  input: DeviceHandshakeInput,
): DeviceHandshake {
  const identity = getDeviceIdentity();
  const signedAt = Date.now();
  const scopesField = input.scopes.join(',');

  const payload = [
    DEVICE_AUTH_PAYLOAD_VERSION,
    identity.id,
    input.clientId,
    input.clientMode,
    input.role,
    scopesField,
    String(signedAt),
    input.token,
    input.nonce,
  ].join('|');

  const signature = identity.sign(payload);

  return {
    id: identity.id,
    publicKey: identity.publicKey,
    signature,
    signedAt,
    nonce: input.nonce,
  };
}
